import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BellRing, BellOff, Send } from "lucide-react";

const b64ToUint8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export default function PushToggle({ description }) {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.register("/sw.js").then((reg) => reg.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, [supported]);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications refusées par le navigateur"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public");
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
      const j = s.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
      setSub(s); toast.success("Alertes push activées sur cet appareil");
    } catch (e) { toast.error("Activation impossible : " + (e.message || "erreur")); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    try {
      await api.delete("/push/subscribe", { params: { endpoint: sub.endpoint } });
      await sub.unsubscribe(); setSub(null); toast.success("Alertes push désactivées");
    } catch { toast.error("Erreur"); }
  };

  const test = async () => { await api.post("/push/test"); toast.success("Notification test envoyée"); };

  if (!supported) return <div className="mt-6 sc-card text-sm text-slate-500" data-testid="push-unsupported">Les alertes push ne sont pas supportées par ce navigateur.</div>;

  return (
    <div className="mt-6 sc-card" data-testid="push-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><BellRing className="w-5 h-5 text-rose-500" /> Alertes push</h2>
          <p className="text-sm text-slate-600 mt-1">{description}</p>
        </div>
        <div className="flex gap-2">
          {sub ? (
            <>
              <button data-testid="push-test" onClick={test} className="sc-btn sc-btn-outline text-sm"><Send className="w-4 h-4" /> Tester</button>
              <button data-testid="push-disable" onClick={disable} className="sc-btn sc-btn-outline text-sm text-rose-700"><BellOff className="w-4 h-4" /> Désactiver</button>
            </>
          ) : (
            <button data-testid="push-enable" disabled={busy} onClick={enable} className="sc-btn sc-btn-primary text-sm disabled:opacity-50"><BellRing className="w-4 h-4" /> Activer sur cet appareil</button>
          )}
        </div>
      </div>
      <div className="mt-2 text-xs" data-testid="push-status">{sub ? <span className="text-emerald-700 font-semibold">Actif sur cet appareil</span> : <span className="text-slate-500">Inactif</span>}</div>
    </div>
  );
}
